import { useCallback, useState } from "react";
import type { FieldProps } from "@/app/servers/types";

type Draft = { id: string; name: string; value: string };

type UpdateDraftOptions = {
  id: string;
  field: "name" | "value";
  value: string;
  cursorPosition?: number;
  inputElement?: HTMLInputElement;
};

export function useHeaderDrafts({ form }: { form: FieldProps["form"] }) {
  const [drafts, setDrafts] = useState<Draft[]>([]);

  const addDraft = useCallback(() => {
    setDrafts((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: "", value: "" },
    ]);
  }, []);

  const removeDraft = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const updateDraft = useCallback((opts: UpdateDraftOptions) => {
    const { id, field, value } = opts;
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, [field]: value } : d))
    );
  }, []);

  const commitDraftIfReady = useCallback(
    (id: string) => {
      const draft = drafts.find((d) => d.id === id);
      if (!draft?.name?.trim()) {
        return;
      }

      const headerName = draft.name.trim();
      form.setValue(`mcp.headers.${headerName}`, draft.value, {
        shouldValidate: false,
      });
      setDrafts((prev) => prev.filter((d) => d.id !== id));
    },
    [drafts, form]
  );

  return {
    drafts,
    setDrafts,
    addDraft,
    removeDraft,
    updateDraft,
    commitDraftIfReady,
  };
}
